import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/ui";
import { ApiError, errorMessage } from "./api";

export interface SaveOptions<Out> {
  /** 保存成功后要重新拉取的查询键前缀。 */
  invalidate: string[];
  /** 成功 Toast 的文案；需要带上接口返回的内容时传函数。 */
  message: string | ((out: Out) => string);
  onSaved?: (out: Out) => void;
  /** 502 时的警告文案，缺省用接口的错误信息。 */
  partial?: (e: ApiError) => string;
}

/** 表单保存：调接口、成功后刷新列表并 Toast，失败把文案交给表单显示。
 * 502 是已保存、只是后续的同步没完成（通知数据面或余额计数器，决策 D57）：同样刷新，给出 warning 而不是 error，表单据此不再允许提交，免得重复写入。 */
export function useSave<In, Out>(fn: (input: In) => Promise<Out>, opts: SaveOptions<Out>) {
  const qc = useQueryClient();
  const toast = useToast();
  const refresh = () => {
    for (const key of opts.invalidate) void qc.invalidateQueries({ queryKey: [key] });
  };
  const m = useMutation({
    mutationFn: fn,
    onSuccess: (out) => {
      refresh();
      toast(typeof opts.message === "function" ? opts.message(out) : opts.message, { tone: "success" });
      opts.onSaved?.(out);
    },
    onError: (e) => {
      if (isPartial(e)) refresh();
    },
  });
  const partial = isPartial(m.error) ? (opts.partial?.(m.error) ?? errorMessage(m.error)) : null;
  return { save: m.mutate, saving: m.isPending, error: m.error && !partial ? errorMessage(m.error) : null, warning: partial, reset: m.reset };
}

function isPartial(e: unknown): e is ApiError {
  return e instanceof ApiError && e.status === 502;
}
